import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import Editor, { EditorProps } from "@monaco-editor/react";
import * as monaco from "monaco-editor";

export interface JsonEditorProps extends EditorProps {
  onFocus?: () => void;
  onBlur?: () => void;
  className?: string;
  disabled?: boolean;
}

const JsonEditor = forwardRef<monaco.editor.IStandaloneCodeEditor | null, JsonEditorProps>(
  ({ onFocus, onBlur, onMount, className, disabled = false, height = "200px", defaultLanguage = "json", options, ...editorProps }, ref) => {
    const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);

    useImperativeHandle(ref, () => editorRef.current as monaco.editor.IStandaloneCodeEditor);

    useEffect(() => {
      monaco.json.jsonDefaults.setDiagnosticsOptions({
        validate: true,
        schemas: [],
        schemaValidation: "error",
        schemaRequest: "warning",
      });
    }, []);

    const handleEditorDidMount = (editor: monaco.editor.IStandaloneCodeEditor) => {
      editorRef.current = editor;

      // Handle focus events
      if (onFocus) {
        editor.onDidFocusEditorText(() => {
          onFocus();
        });
      }

      // Handle blur events
      if (onBlur) {
        editor.onDidBlurEditorText(() => {
          onBlur();
        });
      }

      onMount?.(editor, monaco);
    };

    return (
      <Editor
        height={height}
        defaultLanguage={defaultLanguage}
        onMount={handleEditorDidMount}
        options={{
          minimap: { enabled: false },
          readOnly: disabled,
          ...options,
        }}
        {...editorProps}
      />
    );
  }
);

JsonEditor.displayName = "JsonEditor";

export default JsonEditor;
